import { type IUser } from "src/components/shared/UserList/UserList.types";

import { type UserSelectItem } from "../_Modals/AuthorizationModal/AuthorizationModal";

export const masterRoles = ["Captain/Master", "Master", "Captain"];

export const getActiveMasterId = (
  loggedInUserId: string | null | undefined,
  loggedInRole: string | null | undefined,
): string | undefined =>
  loggedInUserId && masterRoles.includes((loggedInRole ?? "").trim())
    ? loggedInUserId
    : undefined;

const officerRoles = [
  "2nd Officer",
  "3rd Officer",
  "4th Officer",
  "Navigation Officer",
  "Ships Security Officer",
  "OOW",
  "Lookout",
  "Bosun",
  "1st Officer",
  "Chief Officer/First Officer",
];
export const engineerRoles = [
  "2nd Engineer",
  "3rd Engineer",
  "Chief Engineer",
  "Chief Engineer 2",
  "Duty Engineer",
];
/**
 * Maps a list of users to a specific format based on their roles and provided label formats.
 *
 * @param users - An array of user objects (`IUser`) to be mapped.
 * @param unit - A string or array of strings representing the role(s) to filter users by.
 * @param labelFormat - A string template for formatting the label of each user.
 * @param dropdownLabelFormat - (Optional) A string template for formatting the dropdown label of each user.
 * @param additional - (Optional) Additional options for display, such as showing contact numbers.
 * @returns An array of `UserSelectItem` objects containing the mapped user data.
 */
interface IMapUser extends IUser {
  manualValue?: string;
}
export const mapUsers = (
  users: IMapUser[],
  unit: string | string[],
  labelFormat: string,
  dropdownLabelFormat?: string,
  additional?: { showContactNumbers?: boolean },
): UserSelectItem[] => {
  let roles: string[] | null = null;

  if (typeof unit === "string") {
    switch (unit) {
      case "master":
        roles = masterRoles;
        break;
      case "officer":
        roles = officerRoles;
        break;
      case "engineers":
        roles = engineerRoles;
        break;
      default:
        break;
    }
  } else if (Array.isArray(unit)) {
    roles = unit;
  }

  const data: UserSelectItem[] = [];
  users?.forEach(user => {
    interface ISelectUser {
      value: string;
      label: string;
      dropdownLabel?: string;
      usertype: number;
      contactNumber?: string;
      email?: string;
    }
    if (user.manualValue) {
      data.push({
        value: user.userid,
        label: user.manualValue,
        dropdownLabel: user.manualValue,
        usertype: user.usertype,
        email: user.email,
      });
      return;
    }
    const label = labelFormat
      .replaceAll("ref", user.ref ?? "")
      .replaceAll("firstname", user.firstname ?? "")
      .replaceAll("surname", user.surname ?? "")
      .replaceAll("role", user.rolename ?? user.role ?? "")
      .replaceAll("full_name", user.full_name ?? "");

    if (!label.replace("-", "").trim()) return;
    // Get contact number (mobile first, fallback to phone)
    const contactNumber = additional?.showContactNumbers
      ? user.mobile || user.phoneno || "-"
      : undefined;

    const u: ISelectUser = {
      value: user.userid,
      label,
      usertype: user.usertype,
      email: user.email,
      ...(contactNumber && { contactNumber }),
    };

    if (dropdownLabelFormat) {
      u.dropdownLabel = dropdownLabelFormat
        .replaceAll("ref", user.ref ?? "")
        .replaceAll("firstname", user.firstname ?? "")
        .replaceAll("surname", user.surname ?? "")
        .replaceAll("role", user.rolename ?? user.role ?? "");
    }

    if (roles) {
      if (roles.includes(user.rolename)) data.push(u);
    } else {
      data.push(u);
    }
  });
  return data;
};
